import React, { useState, useEffect } from 'react';
import { fetchAPI, postAPI, putAPI, deleteAPI } from '../../utils/utils';
import './QuoteVariantsEditor.css';
import { hasFamiliesFeature } from '../../utils/colla-features';
import { formatMoney } from '../../utils/money';

function QuoteVariantsEditor({ quoteId, quoteImport, onUpdate }) {
    const [variants, setVariants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingVariant, setEditingVariant] = useState(null);
    const [newVariant, setNewVariant] = useState({
        nom: '',
        descripcio: '',
        import: '',
        has_children_addon: false,
        children_price: '',
        max_children: '',
        has_adults_addon: false,
        adult_price: '',
        max_adults: ''
    });
    const [showAddForm, setShowAddForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // Validation function for variant data
    const validateVariant = (variant) => {
        const errors = {};
        
        // Validate name
        if (!variant.nom || variant.nom.trim() === '') {
            errors.nom = 'El nom de la variant és obligatori';
        }
        
        // Validate import
        if (variant.import === '' || variant.import === null || variant.import === undefined) {
            errors.import = 'L\'import és obligatori';
        } else {
            const importValue = parseFloat(variant.import);
            if (isNaN(importValue)) {
                errors.import = 'L\'import ha de ser un número vàlid';
            } else if (importValue < 0) {
                errors.import = 'L\'import no pot ser negatiu';
            }
        }
        
        // Validate children_price if has_children_addon is enabled
        if (variant.has_children_addon) {
            if (variant.children_price === '' || variant.children_price === null || variant.children_price === undefined) {
                errors.children_price = 'El preu per infant és obligatori';
            } else {
                const childrenPrice = parseFloat(variant.children_price);
                if (isNaN(childrenPrice)) {
                    errors.children_price = 'El preu per infant ha de ser un número vàlid';
                } else if (childrenPrice < 0) {
                    errors.children_price = 'El preu per infant no pot ser negatiu';
                }
            }
            
            // Validate max_children if provided
            if (variant.max_children !== '' && variant.max_children !== null && variant.max_children !== undefined) {
                const maxChildren = parseInt(variant.max_children);
                if (isNaN(maxChildren)) {
                    errors.max_children = 'El límit ha de ser un número enter';
                } else if (maxChildren < 1) {
                    errors.max_children = 'El límit mínim és 1';
                }
            }
        }
        
        // Validate adult_price if has_adults_addon is enabled
        if (variant.has_adults_addon) {
            if (variant.adult_price === '' || variant.adult_price === null || variant.adult_price === undefined) {
                errors.adult_price = 'El preu per adult és obligatori';
            } else {
                const adultPrice = parseFloat(variant.adult_price);
                if (isNaN(adultPrice)) {
                    errors.adult_price = 'El preu per adult ha de ser un número vàlid';
                } else if (adultPrice < 0) {
                    errors.adult_price = 'El preu per adult no pot ser negatiu';
                }
            }
            
            // Validate max_adults if provided
            if (variant.max_adults !== '' && variant.max_adults !== null && variant.max_adults !== undefined) {
                const maxAdults = parseInt(variant.max_adults);
                if (isNaN(maxAdults)) {
                    errors.max_adults = 'El límit ha de ser un número enter';
                } else if (maxAdults < 1) {
                    errors.max_adults = 'El límit mínim és 1';
                }
            }
        }
        
        return errors;
    };

    useEffect(() => {
        if (quoteId) {
            fetchVariants();
        }
    }, [quoteId]);

    const fetchVariants = () => {
        setLoading(true);
        fetchAPI(`/quotes/${quoteId}/variants`, data => {
            setVariants(data || []);
            setLoading(false);
        }, false);
    };

    const handleAddVariant = () => {
        const errors = validateVariant(newVariant);
        setValidationErrors(errors);
        
        if (Object.keys(errors).length > 0) {
            return;
        }

        setSaving(true);
        postAPI(`/quotes/${quoteId}/variants`, {
            nom: newVariant.nom.trim(),
            descripcio: newVariant.descripcio?.trim() || null,
            import: parseFloat(newVariant.import),
            has_children_addon: newVariant.has_children_addon,
            children_price: newVariant.has_children_addon ? parseFloat(newVariant.children_price) : null,
            max_children: newVariant.has_children_addon && newVariant.max_children ? parseInt(newVariant.max_children) : null,
            has_adults_addon: newVariant.has_adults_addon,
            adult_price: newVariant.has_adults_addon ? parseFloat(newVariant.adult_price) : null,
            max_adults: newVariant.has_adults_addon && newVariant.max_adults ? parseInt(newVariant.max_adults) : null,
            display_order: variants.length
        }, () => {
            fetchVariants();
            setNewVariant({
                nom: '',
                descripcio: '',
                import: '',
                has_children_addon: false,
                children_price: '',
                max_children: '',
                has_adults_addon: false,
                adult_price: '',
                max_adults: ''
            });
            setValidationErrors({});
            setShowAddForm(false);
            setSaving(false);
            onUpdate && onUpdate();
        }, () => {
            setSaving(false);
            alert('Error creant la variant');
        });
    };

    const handleUpdateVariant = () => {
        const errors = validateVariant(editingVariant);
        setValidationErrors(errors);
        
        if (Object.keys(errors).length > 0) {
            return;
        }

        setSaving(true);
        putAPI(`/quotes/variants/${editingVariant.id}`, {
            nom: editingVariant.nom.trim(),
            descripcio: editingVariant.descripcio?.trim() || null,
            import: parseFloat(editingVariant.import),
            has_children_addon: editingVariant.has_children_addon,
            children_price: editingVariant.has_children_addon ? parseFloat(editingVariant.children_price) : null,
            max_children: editingVariant.has_children_addon && editingVariant.max_children ? parseInt(editingVariant.max_children) : null,
            has_adults_addon: editingVariant.has_adults_addon,
            adult_price: editingVariant.has_adults_addon ? parseFloat(editingVariant.adult_price) : null,
            max_adults: editingVariant.has_adults_addon && editingVariant.max_adults ? parseInt(editingVariant.max_adults) : null
        }, () => {
            fetchVariants();
            setEditingVariant(null);
            setValidationErrors({});
            setSaving(false);
            onUpdate && onUpdate();
        }, () => {
            setSaving(false);
            alert('Error actualitzant la variant');
        });
    };

    const handleDeleteVariant = (variantId) => {
        if (!window.confirm('Segur que vols eliminar aquesta variant?')) {
            return;
        }

        deleteAPI(`/quotes/variants/${variantId}`, () => {
            fetchVariants();
            onUpdate && onUpdate();
        }, (err) => {
            alert(err?.message || 'Error eliminant la variant');
        });
    };

    const moveVariant = (index, direction) => {
        const newVariants = [...variants];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newVariants.length) return;

        [newVariants[index], newVariants[targetIndex]] = [newVariants[targetIndex], newVariants[index]];
        
        const variantIds = newVariants.map(v => v.id);
        postAPI(`/quotes/${quoteId}/variants/reorder`, { variantIds }, () => {
            fetchVariants();
        });
    };

    const renderVariantForm = (variant, setVariant, onSave, onCancel) => (
        <div className="variant-form">
            <div className="variant-form-row">
                <div className="variant-form-field">
                    <label>Nom de la variant</label>
                    <input
                        type="text"
                        placeholder="Ex: Sènior 65+"
                        value={variant.nom}
                        onChange={e => {
                            setVariant({ ...variant, nom: e.target.value });
                            if (validationErrors.nom) {
                                setValidationErrors(prev => ({ ...prev, nom: null }));
                            }
                        }}
                        className={validationErrors.nom ? 'input-error' : ''}
                    />
                    {validationErrors.nom ? (
                        <small className="field-error">{validationErrors.nom}</small>
                    ) : null}
                </div>
                <div className="variant-form-field variant-import-field">
                    <label>{(variant.has_children_addon || variant.has_adults_addon) ? 'Import fixe' : 'Import'}</label>

                    <div className={`import-input-wrapper ${validationErrors.import ? 'input-error' : ''}`}>
                        <input
                            type="text"
                            inputMode="decimal"
                            placeholder={(variant.has_children_addon || variant.has_adults_addon) ? '0' : '25'}
                            value={variant.import}
                            onChange={e => {
                                const value = e.target.value;
                                if (/^\d*[.,]?\d*$/.test(value)) {
                                    setVariant({ ...variant, import: value.replace(',', '.') });
                                    if (validationErrors.import) {
                                        setValidationErrors(prev => ({ ...prev, import: null }));
                                    }
                                }
                            }}
                        />
                        <span className="currency">€</span>
                    </div>
                    {validationErrors.import ? (
                        <small className="field-error">{validationErrors.import}</small>
                    ) : null}
                </div>
            </div>

            <div className="variant-form-row">
                <div className="variant-form-field">
                    <label>Descripció (opcional)</label>
                    <input
                        type="text"
                        placeholder="Ex: Només per gent de +65 anys"
                        value={variant.descripcio || ''}
                        onChange={e => setVariant({ ...variant, descripcio: e.target.value })}
                    />
                </div>
            </div>

            {hasFamiliesFeature() ? (
                <div className="variant-form-row">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={variant.has_children_addon || variant.has_adults_addon}
                            onChange={e => {
                                const isFamily = e.target.checked;
                                setVariant({
                                    ...variant,
                                    has_children_addon: isFamily,
                                    has_adults_addon: isFamily,
                                    children_price: isFamily ? (variant.children_price || '0') : '',
                                    max_children: isFamily ? variant.max_children : '',
                                    adult_price: isFamily ? (variant.adult_price || '0') : '',
                                    max_adults: isFamily ? variant.max_adults : ''
                                });
                                if (!isFamily) {
                                    setValidationErrors(prev => ({
                                        ...prev,
                                        children_price: null,
                                        max_children: null,
                                        adult_price: null,
                                        max_adults: null
                                    }));
                                }
                            }}
                        />
                        <span className="checkmark"></span>
                        Quota familiar
                    </label>
                </div>
            ) : null}

            {(variant.has_children_addon || variant.has_adults_addon) ? (
                <div className="family-quota-fields">
                    {/* Preu per adult */}
                    <div className="variant-form-row children-price-row">
                        <div className="variant-form-field">
                            <label>Preu per adult</label>
                            <div className={`import-input-wrapper ${validationErrors.adult_price ? 'input-error' : ''}`}>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0"
                                    value={variant.adult_price}
                                    onChange={e => {
                                        const value = e.target.value;
                                        if (/^\d*[.,]?\d*$/.test(value)) {
                                            setVariant({ ...variant, adult_price: value.replace(',', '.'), has_adults_addon: true });
                                            if (validationErrors.adult_price) {
                                                setValidationErrors(prev => ({ ...prev, adult_price: null }));
                                            }
                                        }
                                    }}
                                />
                                <span className="currency">€</span>
                            </div>
                            {validationErrors.adult_price ? (
                                <small className="field-error">{validationErrors.adult_price}</small>
                            ) : null}
                        </div>
                        <div className="variant-form-field">
                            <label>Màxim d'adults</label>
                            <div className={`import-input-wrapper ${validationErrors.max_adults ? 'input-error' : ''}`}>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="∞"
                                    value={variant.max_adults}
                                    onChange={e => {
                                        const value = e.target.value;
                                        if (/^\d*$/.test(value)) {
                                            setVariant({ ...variant, max_adults: value });
                                            if (validationErrors.max_adults) {
                                                setValidationErrors(prev => ({ ...prev, max_adults: null }));
                                            }
                                        }
                                    }}
                                />
                            </div>
                            {validationErrors.max_adults ? (
                                <small className="field-error">{validationErrors.max_adults}</small>
                            ) : null}
                            <small className="field-hint">Deixa buit per sense límit</small>
                        </div>
                    </div>

                    {/* Preu per infant */}
                    <div className="variant-form-row children-price-row">
                        <div className="variant-form-field">
                            <label>Preu per infant</label>
                            <div className={`import-input-wrapper ${validationErrors.children_price ? 'input-error' : ''}`}>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0"
                                    value={variant.children_price}
                                    onChange={e => {
                                        const value = e.target.value;
                                        if (/^\d*[.,]?\d*$/.test(value)) {
                                            setVariant({ ...variant, children_price: value.replace(',', '.'), has_children_addon: true });
                                            if (validationErrors.children_price) {
                                                setValidationErrors(prev => ({ ...prev, children_price: null }));
                                            }
                                        }
                                    }}
                                />
                                <span className="currency">€</span>
                            </div>
                            {validationErrors.children_price ? (
                                <small className="field-error">{validationErrors.children_price}</small>
                            ) : null}
                        </div>
                        <div className="variant-form-field">
                            <label>Màxim d'infants</label>
                            <div className={`import-input-wrapper ${validationErrors.max_children ? 'input-error' : ''}`}>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="∞"
                                    value={variant.max_children}
                                    onChange={e => {
                                        const value = e.target.value;
                                        if (/^\d*$/.test(value)) {
                                            setVariant({ ...variant, max_children: value });
                                            if (validationErrors.max_children) {
                                                setValidationErrors(prev => ({ ...prev, max_children: null }));
                                            }
                                        }
                                    }}
                                />
                            </div>
                            {validationErrors.max_children ? (
                                <small className="field-error">{validationErrors.max_children}</small>
                            ) : null}
                            <small className="field-hint">Deixa buit per sense límit</small>
                        </div>
                    </div>
                </div>
            ) : null}

            <div className="variant-form-actions">
                <button 
                    className="save-variant-btn" 
                    onClick={onSave}
                    disabled={saving}
                >
                    {saving ? 'Desant...' : '💾 Desar'}
                </button>
                <button className="cancel-variant-btn" onClick={() => {
                    setValidationErrors({});
                    onCancel();
                }}>
                    Cancel·lar
                </button>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="variants-editor">
                <div className="variants-loading">Carregant variants...</div>
            </div>
        );
    }

    return (
        <div className="variants-editor">
            <div className="variants-header">
                <h4>Variants de preu</h4>
                <p className="variants-description">
                    Defineix diferents opcions de preu per aquesta quota (ex: infants, adults, famílies)
                </p>
            </div>

            {variants.length === 0 ? (
                <div className="variants-empty">
                    <p>Aquesta quota encara no té variants.</p>
                    <p className="hint">Sense variants, s'usarà el preu base de {formatMoney(quoteImport)}€.</p>
                </div>
            ) : (
                <div className="variants-list">
                    {variants.map((variant, index) => (
                        <div key={variant.id} className="variant-item">
                            {editingVariant?.id === variant.id ? (
                                renderVariantForm(
                                    editingVariant,
                                    setEditingVariant,
                                    handleUpdateVariant,
                                    () => setEditingVariant(null)
                                )
                            ) : (
                                <>
                                    <div className="variant-info">
                                        <div className="variant-name">{variant.nom}</div>
                                        {variant.descripcio ? (
                                            <div className="variant-description">{variant.descripcio}</div>
                                        ) : null}
                                        <div className="variant-price">
                                            {formatMoney(variant.import)}€
                                            {variant.has_adults_addon ? (
                                                <span className="children-addon-badge">
                                                    + {formatMoney(variant.adult_price)}€/adult
                                                    {variant.max_adults ? ` (màx ${variant.max_adults})` : ''}
                                                </span>
                                            ) : null}
                                            {variant.has_children_addon ? (
                                                <span className="children-addon-badge">
                                                    + {formatMoney(variant.children_price)}€/infant
                                                    {variant.max_children ? ` (màx ${variant.max_children})` : ''}
                                                </span>
                                            ) : null}
                                        </div>
                                        {variant.usage_count > 0 ? (
                                            <div className="variant-usage">
                                                {variant.usage_count} pagament{variant.usage_count !== 1 ? 's' : ''}
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className="variant-actions">
                                        <button 
                                            className="variant-action-btn move-btn"
                                            onClick={() => moveVariant(index, -1)}
                                            disabled={index === 0}
                                            title="Moure amunt"
                                        >
                                            ↑
                                        </button>
                                        <button 
                                            className="variant-action-btn move-btn"
                                            onClick={() => moveVariant(index, 1)}
                                            disabled={index === variants.length - 1}
                                            title="Moure avall"
                                        >
                                            ↓
                                        </button>
                                        <button 
                                            className="variant-action-btn edit-btn"
                                            onClick={() => {
                                                setValidationErrors({});
                                                setEditingVariant({
                                                    ...variant,
                                                    import: variant.import?.toString() || '',
                                                    children_price: variant.children_price?.toString() || '',
                                                    max_children: variant.max_children?.toString() || '',
                                                    adult_price: variant.adult_price?.toString() || '',
                                                    max_adults: variant.max_adults?.toString() || ''
                                                });
                                            }}
                                            title="Editar"
                                        >
                                            ✏️
                                        </button>
                                        <button 
                                            className="variant-action-btn delete-btn"
                                            onClick={() => handleDeleteVariant(variant.id)}
                                            disabled={variant.usage_count > 0}
                                            title={variant.usage_count > 0 ? 'No es pot eliminar (té pagaments)' : 'Eliminar'}
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {showAddForm ? (
                <div className="add-variant-form">
                    <h5>Nova variant</h5>
                    {renderVariantForm(
                        newVariant,
                        setNewVariant,
                        handleAddVariant,
                        () => {
                            setShowAddForm(false);
                            setNewVariant({
                                nom: '',
                                descripcio: '',
                                import: '',
                                has_children_addon: false,
                                children_price: '',
                                max_children: '',
                                has_adults_addon: false,
                                adult_price: '',
                                max_adults: ''
                            });
                        }
                    )}
                </div>
            ) : (
                <button 
                    className="add-variant-btn"
                    onClick={() => {
                        setValidationErrors({});
                        setShowAddForm(true);
                    }}
                >
                    ➕ Afegir variant
                </button>
            )}

            {variants.length > 0 ? (
                <div className="variants-note">
                    <strong>Nota:</strong> Els usuaris veuran totes les variants i podran triar quina pagar.
                    Per a variants familiars amb infants, l'usuari indicarà quants infants té.
                </div>
            ) : null}
        </div>
    );
}

export default QuoteVariantsEditor;
